import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, from } from 'rxjs';
import {
  catchError,
  exhaustMap,
  groupBy,
  map,
  mergeMap,
  withLatestFrom
} from 'rxjs/operators';
import { CatalogService } from '../../services/catalog/catalog.service';
import {
  CloudScriptService
} from '../../services/cloud-script/cloud-script.service';
import { getProfile, getProfileSuccess, getProfiles } from './profiles.actions';
import { ProfilesState } from './profiles.reducer';

@Injectable()
export class ProfileEffects {
  private actions$ = inject(Actions);
  private catalogService = inject(CatalogService);
  private cloudScriptService = inject(CloudScriptService);
  private store = inject<Store<{
    profiles: ProfilesState;
}>>(Store);

  getProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getProfile),
      withLatestFrom(this.store.select((state) => state.profiles)), // Access the profiles state
      groupBy(([action, profilesState]) => action.Id), // Group actions by Id
      mergeMap((group$) =>
        group$.pipe(
          exhaustMap(([action, profilesState]) => {
            // Ignore new actions for the same Id while processing
            const id = action.Id;
            const profile = profilesState.data.find(
              (profile) => profile.id === id,
            ); // Find the profile in the state

            if (!profile) {
              return this.cloudScriptService
                .getItemOwnerInfo(action.Id, action.Type)
                .pipe(
                  map((response) => {
                    const functionResult = response.data.FunctionResult;

                    return getProfileSuccess({
                      payload: {
                        id: action.Id,
                        username: functionResult.username,
                        playFabId: functionResult.playFabId,
                        DisplayName: functionResult.DisplayName,
                        avatarUrl: functionResult.avatarUrl,
                      },
                    });
                  }),
                  catchError((error) => {
                    this.store.dispatch({
                      type: '[Profile] Error Get Profile',
                      payload: { error: error.message },
                    });
                    return EMPTY;
                  }),
                );
            } else {
              // If profile exists, you might want to return a different action or do nothing
              return EMPTY;
            }
          }),
        ),
      ),
    ),
  );

  getProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getProfiles),
      withLatestFrom(this.store.select((state) => state.profiles)), // Access the profiles state
      mergeMap(([action, profilesState]) => {
        // Remove duplicates from the array
        const uniqueProfiles = action.profiles.filter(
          (currentValue, currentIndex, originalArray) => {
            return (
              originalArray.findIndex((item) => item.Id === currentValue.Id) ===
              currentIndex
            );
          },
        );

        return from(uniqueProfiles).pipe(
          // Convert the array into an Observable
          mergeMap((profile) => {
            const id = profile.Id;
            const profileInState = profilesState.data.find((p) => p.id === id); // Find the profile in the state

            if (!profileInState) {
              return this.cloudScriptService
                .getItemOwnerInfo(profile.Id, profile.Type)
                .pipe(
                  map((response) => {
                    const functionResult = response.data.FunctionResult;
                    return getProfileSuccess({
                      payload: {
                        id: profile.Id,
                        username: functionResult.username,
                        playFabId: functionResult.playFabId,
                        DisplayName: functionResult.DisplayName,
                        avatarUrl: functionResult.avatarUrl,
                      },
                    });
                  }),
                  catchError((error) => {
                    this.store.dispatch({
                      type: '[Profile] Error Get Profile',
                      payload: { error: error.message },
                    });
                    return EMPTY;
                  }),
                );
            } else {
              // If profile exists, you might want to return a different action or do nothing
              return EMPTY;
            }
          }),
        );
      }),
    ),
  );
}
